import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import sanitizeHtml from '../utils/sanitizeHtml';
import PortraitDialogue from './PortraitDialogue';
import './GameLog.css';

const GameLog = forwardRef(({ character, onConversationChoice, onConversationClose }, ref) => {
  const [logEntries, setLogEntries] = useState([]);
  const [pendingChoices, setPendingChoices] = useState(null);
  const [dialogueContext, setDialogueContext] = useState(null);
  const logRef = useRef();

  // Add a new log entry
  const addLogEntry = (entry) => {
    setLogEntries(prev => [...prev, {
      id: Date.now(),
      timestamp: new Date(),
      ...entry
    }]);
  };

  // Auto-scroll to bottom when new entries are added
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logEntries]);

  // Auto-scroll the game log when new choices appear so the latest dialogue
  // text remains visible above the portrait dialogue overlay.
  useEffect(() => {
    if (pendingChoices && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [pendingChoices]);

  // Expose functions to parent components
  useImperativeHandle(ref, () => ({
    addLogEntry,
    setPendingChoices,
    setDialogueContext,
    clearDialogue: () => {
      setPendingChoices(null);
      setDialogueContext(null);
    }
  }));

  const handleChoice = (choiceIndex) => {
    if (onConversationChoice && pendingChoices && pendingChoices[choiceIndex]) {
      onConversationChoice(pendingChoices[choiceIndex]);
    }
    setPendingChoices(null);
  };

  return (
    <div className="game-log">
      <div className="game-log-header">
        <h3>Game Log</h3>
      </div>
      <div
        className="game-log-content"
        ref={logRef}
      >
        {logEntries.length === 0 && (
          <div className="log-entry welcome">
            <div className="log-text">Welcome to the Realms of Loria!</div>
            <div className="log-text">Move around to explore and interact with NPCs.</div>
          </div>
        )}
        
        {logEntries.map(entry => (
          <div key={entry.id} className={`log-entry ${entry.type || 'info'}`}>
            <div className="log-timestamp">
              {entry.timestamp.toLocaleTimeString()}
            </div>
            <div className="log-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(entry.text) }} />
          </div>
        ))}
      </div>

      {(pendingChoices || dialogueContext) && (
        <PortraitDialogue
          context={dialogueContext}
          choices={pendingChoices || []}
          onSelect={handleChoice}
          onClose={onConversationClose}
        />
      )}
    </div>
  );
});

export default GameLog;
